import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AppService } from '@app/app.service';
import { InformModalComponent } from '@modules/shared/modal/inform-modal/inform-modal.component';
import { DialogService } from '@modules/shared/services/dialog.service';
import { HeaderService } from '@modules/shared/services/header.service';
import { LocaleService } from '@modules/shared/services/locale.service';
import { MagazinePageService } from '@pages/magazine-page/magazine-page.service';
import { Magazine } from '@pages/magazine-page/models/magazine.model';
import { TopicPageService } from '@pages/magazine-page/topic-page.service';
import { Locale } from 'src/environments/language.config';

// TODO: refactor this into a "page" feature module with a "page" resolver
//       that can switch between topic / content-hub / page / event-page
@Injectable({
  providedIn: 'root',
})
export class MagazineResolver implements Resolve<Observable<Magazine | null>> {
  private appService = inject(AppService);
  private dialogService = inject(DialogService);
  private localeService = inject(LocaleService);
  private magazinePageService = inject(MagazinePageService);
  private topicPageService = inject(TopicPageService);
  private router = inject(Router);
  private headerService = inject(HeaderService);
  private translateService = inject(TranslateService);

  public resolve(route: ActivatedRouteSnapshot): Observable<Magazine | null> {
    // fallback to legacy route using :slug
    const level1 = route.paramMap.get('level1') || route.paramMap.get('slug') || 'home';
    const level2 = route.paramMap.get('level2') || undefined;
    const level3 = route.paramMap.get('level3') || undefined;
    const level4 = route.paramMap.get('level4') || undefined;

    const path = [level1, level2, level3, level4].filter((l) => l !== undefined).join('/');
    const absolutePath = '/' + path;

    // check if this path has a topic (navigation item from menu.json)
    // else we resolve a legacy magazine / event page
    return this.headerService.findNavigationMenuByPath(absolutePath).pipe(
      switchMap((matchedMenuItem) => {
        if (matchedMenuItem) {
          if (matchedMenuItem.show_topic_page) {
            // show topic page
            return this.topicPageService.getTopicPage(absolutePath).pipe(
              map((topicPage) => {
                this.appService.darkBackgroundPage();

                if (!topicPage) {
                  // fallback, go to content hub
                  this.router.navigate(['content', ...path.split('/')]);
                  return null;
                } else {
                  this.appService.setTitle(`${topicPage.title} | Mercedes-Benz Media`);
                  return topicPage;
                }
              })
            );
          } else {
            // show content hub (default)
            this.router.navigate(['content', ...path.split('/')]);
            return EMPTY;
          }
        } else {
          // fallback to magazine / event page
          return this.magazinePageService.getMagazine(level1).pipe(
            map((magazine) => {
              if (!magazine) {
                throw new HttpErrorResponse({ status: 404, statusText: 'Magazine page not found' });
              } else {
                this.appService.darkBackgroundPage();
                this.appService.setTitle(
                  level1 === 'home' ? 'Mercedes-Benz Media' : `${magazine.title} | Mercedes-Benz Media`
                );
                return magazine;
              }
            })
          );
        }
      }),
      catchError((err) => {
        if (err.status === 401) {
          this.router.navigate(['401'], { skipLocationChange: true });
        }
        if (err.status === 403) {
          this.router.navigate(['403'], { skipLocationChange: true });
        }
        if (err.status === 404) {
          this.router.navigate(['404'], { skipLocationChange: true });
        }
        if (err.status === 406) {
          // NO_TRANSLATION, mirrors ArticleResolver/PressKitResolver/PreMediaResolver
          this.openNoTranslationModal();
        }
        return EMPTY;
      })
    );
  }

  private openNoTranslationModal() {
    const isEnglish = this.translateService.getCurrentLang() === Locale.EN;
    const targetLanguage = isEnglish ? Locale.DE : Locale.EN;

    const actionLabel =
      targetLanguage === Locale.DE ? 'ERRORS.API.NO_TRANSLATION.ACTION_DE' : 'ERRORS.API.NO_TRANSLATION.ACTION_EN';

    this.dialogService.openNext({
      component: InformModalComponent,
      disableCloseBtn: true,
      disableBackdropClose: true,
      inputs: {
        headline: 'ERRORS.API.NO_TRANSLATION.HEADLINE',
        message: 'ERRORS.API.NO_TRANSLATION.TEXT',
        actionLabel,
      },
      outputs: {
        requestAction: () => {
          this.dialogService.close();
          this.localeService.changeLanguage(targetLanguage);
        },
      },
    });
  }
}
